import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Variation Modal Component
 *
 * Premium bottom-sheet modal for selecting product variations.
 * Renders as an overlay within the chat widget (60% height), using a portal.
 *
 * @package WooAIChatbot
 */
import { useState, useMemo } from 'react';
// Decode HTML entities (e.g. &nbsp;1,250&#8362; -> 1,250₪)
const decodeHtml = (html) => {
    const el = document.createElement('span');
    el.innerHTML = html;
    return el.textContent || el.innerText || html;
};
import { createPortal } from 'react-dom';
import { X, ShoppingCart, Loader2, Check } from 'lucide-react';
import { wac } from '../utils/wacClassNames';
import { useTranslation } from '../../hooks/useTranslation';
export const VariationModal = ({ isOpen, onClose, product, onAddToCart, }) => {
    const { t } = useTranslation();
    const [selectedAttributes, setSelectedAttributes] = useState({});
    const [isAdding, setIsAdding] = useState(false);
    // Filter variations matching currently selected attributes (partial or full match).
    const filteredVariations = useMemo(() => {
        if (!product.variations || !product.attributes)
            return [];
        const selectedKeys = Object.keys(selectedAttributes);
        if (selectedKeys.length === 0)
            return product.variations;
        return product.variations.filter((variation) => selectedKeys.every((key) => {
            const sel = selectedAttributes[key];
            const val = variation.attributes[key];
            return val === sel || val === '';
        }));
    }, [selectedAttributes, product.variations, product.attributes]);
    // Exact match: only when ALL attributes are selected.
    const matchingVariation = useMemo(() => {
        if (!product.attributes)
            return null;
        const allSelected = product.attributes.every((attr) => selectedAttributes[attr.label]);
        if (!allSelected)
            return null;
        return filteredVariations[0] || null;
    }, [filteredVariations, selectedAttributes, product.attributes]);
    // Display price: exact match > range from filtered > base price.
    const displayPrice = useMemo(() => {
        if (matchingVariation)
            return matchingVariation.price_formatted;
        if (filteredVariations.length > 0 && Object.keys(selectedAttributes).length > 0) {
            const prices = filteredVariations.map((v) => parseFloat(String(v.price))).filter((p) => !isNaN(p));
            if (prices.length === 0)
                return product.price_formatted || '—';
            const min = Math.min(...prices);
            const max = Math.max(...prices);
            if (min === max)
                return filteredVariations[0].price_formatted;
            const minVar = filteredVariations.reduce((a, b) => parseFloat(String(a.price)) < parseFloat(String(b.price)) ? a : b);
            const maxVar = filteredVariations.reduce((a, b) => parseFloat(String(a.price)) > parseFloat(String(b.price)) ? a : b);
            return `${minVar.price_formatted} – ${maxVar.price_formatted}`;
        }
        return product.price_formatted || '—';
    }, [matchingVariation, filteredVariations, selectedAttributes, product.price_formatted]);
    const handleAttributeSelect = (attributeLabel, value) => {
        setSelectedAttributes((prev) => ({
            ...prev,
            [attributeLabel]: value,
        }));
    };
    const handleAddToCart = async () => {
        if (!matchingVariation)
            return;
        setIsAdding(true);
        try {
            await onAddToCart(product.id, matchingVariation.id);
            onClose();
        }
        catch (error) {
            console.error('Failed to add to cart:', error);
        }
        finally {
            setIsAdding(false);
        }
    };
    if (!isOpen)
        return null;
    // Render into the widget panel via portal so position:absolute works correctly
    const portalTarget = document.getElementById('wac-widget-panel');
    if (!portalTarget)
        return null;
    const canAddToCart = matchingVariation && matchingVariation.in_stock;
    // Show variation image: exact match > single filtered variation > base product image
    const filteredImage = filteredVariations.length === 1 ? filteredVariations[0].image_url : undefined;
    const displayImage = matchingVariation?.image_url || filteredImage || product.image ||
        'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="300"%3E%3Crect fill="%23f3f4f6" width="300" height="300"/%3E%3C/svg%3E';
    const selectedCount = Object.keys(selectedAttributes).length;
    const totalAttrs = product.attributes?.length || 0;
    const modalContent = (_jsxs("div", { style: {
            position: 'absolute',
            inset: 0,
            zIndex: 100,
            display: 'flex',
            alignItems: 'flex-end',
            borderRadius: '16px 16px 0 0',
            overflow: 'hidden',
        }, onClick: onClose, children: [_jsx("div", { style: {
                    position: 'absolute',
                    inset: 0,
                    background: 'rgba(0, 0, 0, 0.4)',
                    backdropFilter: 'blur(4px)',
                    WebkitBackdropFilter: 'blur(4px)',
                } }), _jsxs("div", { onClick: (e) => e.stopPropagation(), style: {
                    position: 'relative',
                    width: '100%',
                    maxHeight: '65%',
                    background: 'white',
                    borderRadius: '20px 20px 0 0',
                    boxShadow: '0 -8px 32px rgba(0, 0, 0, 0.12), 0 -2px 8px rgba(0, 0, 0, 0.08)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }, children: [_jsx("div", { style: { display: 'flex', justifyContent: 'center', paddingTop: '12px', paddingBottom: '4px' }, children: _jsx("div", { style: {
                                width: '36px',
                                height: '4px',
                                borderRadius: '2px',
                                background: 'var(--waa-gray-300, #D1D5DB)',
                            } }) }), _jsx("button", { onClick: onClose, style: {
                            position: 'absolute',
                            right: '16px',
                            top: '12px',
                            zIndex: 10,
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '50%',
                            border: 'none',
                            background: 'var(--waa-gray-100, #F3F4F6)',
                            cursor: 'pointer',
                            transition: 'background 0.2s',
                        }, onMouseEnter: (e) => e.currentTarget.style.background = 'var(--waa-gray-200, #E5E7EB)', onMouseLeave: (e) => e.currentTarget.style.background = 'var(--waa-gray-100, #F3F4F6)', children: _jsx(X, { className: wac('w-4 h-4'), style: { color: 'var(--waa-gray-600, #4B5563)' } }) }), _jsxs("div", { style: { flex: 1, overflowY: 'auto', padding: '8px 24px 20px' }, children: [_jsxs("div", { style: {
                                    display: 'flex',
                                    gap: '16px',
                                    marginBottom: '24px',
                                    alignItems: 'center',
                                }, children: [_jsx("div", { style: {
                                            width: '88px',
                                            height: '88px',
                                            borderRadius: '14px',
                                            overflow: 'hidden',
                                            flexShrink: 0,
                                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                                            border: '1px solid var(--waa-gray-100, #F3F4F6)',
                                        }, children: _jsx("img", { src: displayImage, alt: product.name, style: {
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                display: 'block',
                                            } }) }), _jsxs("div", { style: { flex: 1, minWidth: 0 }, children: [_jsx("h3", { style: {
                                                    fontSize: '15px',
                                                    fontWeight: 600,
                                                    color: 'var(--waa-gray-900, #111827)',
                                                    margin: 0,
                                                    lineHeight: 1.35,
                                                    letterSpacing: '-0.01em',
                                                }, children: product.name }), _jsx("p", { style: {
                                                    fontSize: '20px',
                                                    fontWeight: 700,
                                                    color: 'var(--waa-primary)',
                                                    margin: '6px 0 0',
                                                    lineHeight: 1.2,
                                                    letterSpacing: '-0.02em',
                                                }, children: decodeHtml(displayPrice) }), matchingVariation && !matchingVariation.in_stock && (_jsx("p", { style: {
                                                    fontSize: '12px',
                                                    color: 'var(--waa-error, #dc2626)',
                                                    margin: '4px 0 0',
                                                    fontWeight: 500,
                                                }, children: t('outOfStock') }))] })] }), product.attributes?.map((attribute, attrIndex) => (_jsxs("div", { style: { marginBottom: attrIndex < totalAttrs - 1 ? '20px' : '0' }, children: [_jsxs("div", { style: {
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            marginBottom: '10px',
                                        }, children: [_jsx("label", { style: {
                                                    fontSize: '13px',
                                                    fontWeight: 600,
                                                    color: 'var(--waa-gray-800, #1F2937)',
                                                    letterSpacing: '-0.01em',
                                                }, children: attribute.label }), selectedAttributes[attribute.label] && (_jsx("span", { style: {
                                                    fontSize: '12px',
                                                    color: 'var(--waa-primary)',
                                                    fontWeight: 500,
                                                }, children: attribute.options.find(o => o.slug === selectedAttributes[attribute.label])?.name }))] }), _jsx("div", { style: { display: 'flex', flexWrap: 'wrap', gap: '8px' }, children: attribute.options.map((option) => {
                                            const isSelected = selectedAttributes[attribute.label] === option.slug;
                                            return (_jsxs("button", { onClick: () => handleAttributeSelect(attribute.label, option.slug), style: {
                                                    padding: '8px 16px',
                                                    borderRadius: '10px',
                                                    border: `1.5px solid ${isSelected ? 'var(--waa-primary)' : 'var(--waa-gray-200, #E5E7EB)'}`,
                                                    backgroundColor: isSelected ? 'var(--waa-primary-50, rgba(59, 130, 246, 0.08))' : 'white',
                                                    color: isSelected ? 'var(--waa-primary)' : 'var(--waa-gray-700, #374151)',
                                                    fontWeight: isSelected ? 600 : 400,
                                                    fontSize: '13px',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.15s ease',
                                                    outline: 'none',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    boxShadow: isSelected ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none',
                                                }, children: [isSelected && (_jsx(Check, { style: { width: '14px', height: '14px', strokeWidth: 2.5 } })), option.name] }, option.slug));
                                        }) })] }, attribute.name)))] }), _jsxs("div", { style: {
                            padding: '16px 24px 20px',
                            borderTop: '1px solid var(--waa-gray-100, #F3F4F6)',
                            background: 'var(--waa-gray-50, #F9FAFB)',
                        }, children: [totalAttrs > 1 && !matchingVariation && (_jsxs("div", { style: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    marginBottom: '12px',
                                }, children: [_jsx("div", { style: {
                                            flex: 1,
                                            height: '3px',
                                            borderRadius: '2px',
                                            background: 'var(--waa-gray-200, #E5E7EB)',
                                            overflow: 'hidden',
                                        }, children: _jsx("div", { style: {
                                                width: `${(selectedCount / totalAttrs) * 100}%`,
                                                height: '100%',
                                                borderRadius: '2px',
                                                background: 'var(--waa-primary)',
                                                transition: 'width 0.3s ease',
                                            } }) }), _jsxs("span", { style: {
                                            fontSize: '11px',
                                            color: 'var(--waa-gray-500, #6B7280)',
                                            fontWeight: 500,
                                            whiteSpace: 'nowrap',
                                        }, children: [selectedCount, "/", totalAttrs] })] })), _jsx("button", { onClick: handleAddToCart, disabled: !canAddToCart || isAdding, style: {
                                    width: '100%',
                                    padding: '14px 20px',
                                    borderRadius: '14px',
                                    border: 'none',
                                    fontSize: '15px',
                                    fontWeight: 600,
                                    color: canAddToCart ? 'white' : 'var(--waa-gray-500, #6B7280)',
                                    background: canAddToCart
                                        ? 'var(--waa-primary)'
                                        : 'var(--waa-gray-200, #E5E7EB)',
                                    cursor: canAddToCart ? 'pointer' : 'default',
                                    transition: 'all 0.2s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                    letterSpacing: '-0.01em',
                                    boxShadow: canAddToCart
                                        ? '0 4px 12px rgba(59, 130, 246, 0.25)'
                                        : 'none',
                                }, onMouseEnter: (e) => {
                                    if (canAddToCart)
                                        e.currentTarget.style.filter = 'brightness(0.92)';
                                }, onMouseLeave: (e) => {
                                    e.currentTarget.style.filter = 'brightness(1)';
                                }, children: isAdding ? (_jsxs(_Fragment, { children: [_jsx(Loader2, { style: { width: '18px', height: '18px', animation: 'spin 1s linear infinite' } }), " ", t('adding')] })) : !matchingVariation ? (t('selectVariation')) : !matchingVariation.in_stock ? (t('outOfStock')) : (_jsxs(_Fragment, { children: [_jsx(ShoppingCart, { style: { width: '18px', height: '18px' } }), " ", t('addToCart')] })) })] })] })] }));
    return createPortal(modalContent, portalTarget);
};
